import { Trans, useLingui } from '@lingui/react/macro';
import { Tag, Typography } from '@remarkable/ark-web';

import { getRouteApi } from '@tanstack/react-router';
import { SubscriptionCancellationFlowCompleted } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import { Button, NotificationBox } from 'src/components';
import { Link2 } from 'src/components/Link2';
import { LinkExternal } from 'src/components/LinkExternal';
import { URLS } from 'src/utils/urls/urls';
import type { ValuePropItem } from 'src/utils/valueProps';
import { valueProps } from 'src/utils/valueProps';

const routeApi = getRouteApi('/_auth/_layout/subscriptions/plan');

export const CancelConnectConfirm = ({
  isCancelling,
  cancelError,
  onCancel,
}: {
  isCancelling: boolean;
  cancelError: Error | null;
  onCancel: (onSuccess: () => void) => void;
}) => {
  const { t } = useLingui();
  const navigate = routeApi.useNavigate();

  const handleCancelConnect = () => {
    onCancel(() => {
      tracker.trackEvent(
        new SubscriptionCancellationFlowCompleted({
          component_location: ComponentLocations.MRF_MODAL.MRF_STEP_1,
          step_number: 1,
          total_steps: 1,
        })
      );

      void navigate({ search: { cancel_subscription: 'survey' } });
    });
  };

  return (
    <div
      className="flex h-full flex-col justify-between text-center"
      data-cy="mrf-step-1"
    >
      <div className="ls:pt-64">
        <Typography variant="heading-subtle-xl">
          <Trans>You&apos;ll lose</Trans>
        </Typography>
        <Typography
          variant="responsive-heading-md"
          className="mt-8 text-pretty lm:mt-16"
          as="h1"
        >
          <Trans>
            Access to exclusive templates, seamless integrations, and a<br />
            growing list of tools built to suit your workflow
          </Trans>
        </Typography>
        <Typography className="mt-16 lm:mt-32" variant="body-md">
          <Trans>
            Read more about the benefits with Connect{' '}
            <LinkExternal
              inline
              to={URLS.RM_COM_STORE_CONNECT}
              componentLocation={ComponentLocations.MRF_MODAL.MRF_STEP_1}
            >
              here
            </LinkExternal>
            .
          </Trans>
        </Typography>
        <ul className="mx-auto my-32 grid w-fit grid-cols-1 gap-16 lm:my-48 lm:grid-cols-4 lm:gap-48">
          {Object.values(valueProps.connectCancellation).map(
            (valueProp: ValuePropItem) => (
              <li
                key={valueProp.id}
                className="flex w-full flex-row items-center justify-start gap-x-8 gap-y-12 self-baseline lm:max-w-[168px] lm:flex-col lm:items-center lm:justify-center lm:gap-y-8"
              >
                <div className="flex size-64 shrink-0 items-center justify-center lm:size-56">
                  {valueProp.pictogram && (
                    <valueProp.pictogram className="size-64 shrink-0" />
                  )}
                </div>

                <Typography
                  className="text-left lm:text-center lm:text-pretty"
                  variant="body-sm"
                >
                  {valueProp.title}
                  {'badge' in valueProp && (
                    <Tag variant="info-light" className="ml-8 inline">
                      {valueProp.badge}
                    </Tag>
                  )}
                </Typography>
              </li>
            )
          )}
        </ul>
      </div>
      {cancelError && (
        <NotificationBox
          error={cancelError}
          title={t`Something went wrong`}
          className="mt-16"
        >
          <Trans>
            Please try again or contact support if the problem persists.
          </Trans>
        </NotificationBox>
      )}
      <div className="flex w-full flex-col items-center justify-center gap-24 pb-32 lm:flex-row lm:gap-32">
        <Link2
          className="w-full ls:w-fit"
          size="large"
          variant="primary"
          as="button"
          to="."
          search={{ cancel_subscription: undefined }}
          componentLocation={ComponentLocations.MRF_MODAL.MRF_STEP_1}
        >
          <Trans>Keep Connect</Trans>
        </Link2>

        <Button
          as="button"
          variant="secondary"
          data-cy="confirm-cancel-subscription"
          onClick={handleCancelConnect}
          loading={isCancelling}
        >
          <>
            <span>
              <Trans>Confirm</Trans>
            </span>
          </>
        </Button>
      </div>
    </div>
  );
};
